import { AxiosError } from 'axios';

interface ValidationDetail {
  loc?: (string | number)[];
  msg?: string;
  type?: string;
}

const FIELD_LABELS: Record<string, string> = {
  ip_adresse: 'IP-Adresse',
};

function cleanValidationMessage(msg: string | undefined): string {
  if (!msg) return '';
  return msg.replace(/^Value error,\s*/i, '');
}

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function validationItems(error: unknown): ValidationDetail[] {
  const axiosErr = error as AxiosError<{ detail?: string | ValidationDetail[] }>;
  const detail = axiosErr.response?.data?.detail;
  return Array.isArray(detail) ? detail : [];
}

/**
 * Extracts a human-readable error message from an Axios error response.
 * Handles FastAPI's `detail` format (string or 422 validation array).
 */
export function extractApiError(error: unknown, fallback?: string): string {
  if (!error) {
    return fallback ?? 'Ein unerwarteter Fehler ist aufgetreten';
  }

  const axiosErr = error as AxiosError<{ detail?: string | ValidationDetail[] }>;

  if (!axiosErr.response) {
    if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
      return 'Netzwerkfehler — Server nicht erreichbar';
    }
    return fallback ?? 'Ein unerwarteter Fehler ist aufgetreten';
  }

  const { data, status } = axiosErr.response;
  const detail = data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((d) => {
        const field = d.loc
          ? d.loc.filter((segment) => segment !== 'body').join('.')
          : '';
        const msg = cleanValidationMessage(d.msg);
        return field ? `${fieldLabel(field)}: ${msg}` : msg;
      })
      .filter(Boolean)
      .join('\n');
  }

  if (status === 500) {
    return 'Interner Serverfehler';
  }

  return fallback ?? `Fehler (HTTP ${status})`;
}

/** Liest die Validierungsmeldung für ein Request-Body-Feld aus einer 422-Antwort. */
export function extractFieldError(error: unknown, field: string): string | null {
  for (const item of validationItems(error)) {
    const loc = (item.loc ?? []).filter((segment) => segment !== 'body');
    if (loc[loc.length - 1] === field) {
      const msg = cleanValidationMessage(item.msg);
      return msg || null;
    }
  }
  return null;
}
